// useColumnLayout.js — per-user table column layout (order / width / hidden) with
// server-side persistence. Reusable across Positions, Market Watch and Orders.
//
//   const cols = useColumnLayout('positions_columns', POS_COLUMNS);
//   <Table columns={cols.columns} onColumnResize={cols.resize} ... />
//   // cols.layout / cols.toggle / cols.move / cols.reset drive the "Columns" panel
//
// Behaviour:
//  - Loads the saved layout for `prefKey` from /api/preferences/:key on mount.
//  - Merges with `baseColumns` — the CODE is the source of truth for WHICH columns
//    exist: a column ADDED in code appears for every user (appended, visible); a column
//    REMOVED in code disappears for every user; saved settings only govern order/width/
//    hidden for columns that still exist. (Matches the agreed future-proofing rule.)
//  - Saves 30s after the last change (debounced) and flushes on tab-hide / unmount so a
//    quick change is never lost. Stored per-user, so the layout follows the user across
//    devices/browsers.
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';

const SAVE_DELAY_MS = 30000; // 30s debounce (per Shubh)

function defaultLayout(baseColumns) {
  return baseColumns.map(c => ({ key: c.key, width: c.width, hidden: false }));
}

// Merge a saved layout with the current base column set.
function buildLayout(saved, baseColumns) {
  const baseByKey = new Map(baseColumns.map(c => [c.key, c]));
  const result = [];
  const seen = new Set();
  if (Array.isArray(saved)) {
    for (const s of saved) {
      if (s && baseByKey.has(s.key) && !seen.has(s.key)) {
        const base = baseByKey.get(s.key);
        result.push({
          key: s.key,
          width: (typeof s.width === 'number' && s.width > 0) ? s.width : base.width,
          hidden: !!s.hidden,
        });
        seen.add(s.key);
      }
    }
  }
  // Append any base columns the saved layout didn't have (new columns) — visible.
  for (const c of baseColumns) {
    if (!seen.has(c.key)) { result.push({ key: c.key, width: c.width, hidden: false }); seen.add(c.key); }
  }
  return result;
}

export default function useColumnLayout(prefKey, baseColumns) {
  const [layout, setLayout] = useState(() => defaultLayout(baseColumns));
  const loadedRef = useRef(false);
  const saveTimerRef = useRef(null);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  const baseByKey = useMemo(() => new Map(baseColumns.map(c => [c.key, c])), [baseColumns]);

  const save = useCallback((lay) => {
    axios.put(`/api/preferences/${prefKey}`, { value: lay }).catch(() => {});
  }, [prefKey]);

  // Load saved layout once on mount.
  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/preferences/${prefKey}`)
      .then(res => {
        if (cancelled) return;
        const saved = res.data && res.data.value;
        setLayout(buildLayout(saved, baseColumns));
      })
      .catch(() => { /* keep default layout on error */ })
      .finally(() => { loadedRef.current = true; });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefKey]);

  // Debounced save whenever the layout changes after the initial load.
  useEffect(() => {
    if (!loadedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      save(layoutRef.current);
      saveTimerRef.current = null;
    }, SAVE_DELAY_MS);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [layout, save]);

  // Flush a pending save on tab-hide / unmount so a quick change isn't lost.
  useEffect(() => {
    const flush = () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        save(layoutRef.current);
      }
    };
    const onVis = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('beforeunload', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, [save]);

  // Effective columns to render: visible, in order, merged with base defs + saved widths.
  const columns = useMemo(() =>
    layout.filter(l => !l.hidden && baseByKey.has(l.key)).map(l => ({ ...baseByKey.get(l.key), width: l.width })),
    [layout, baseByKey]);

  const resize = useCallback((key, width) => {
    setLayout(prev => prev.map(l => l.key === key ? { ...l, width } : l));
  }, []);
  const toggle = useCallback((key) => {
    setLayout(prev => prev.map(l => l.key === key ? { ...l, hidden: !l.hidden } : l));
  }, []);
  const move = useCallback((key, dir) => {
    setLayout(prev => {
      const i = prev.findIndex(l => l.key === key);
      if (i < 0) return prev;
      const j = dir < 0 ? i - 1 : i + 1;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }, []);
  // Drag-and-drop reorder: move `key` to `targetIndex` in one jump (vs `move`'s one-step).
  const moveTo = useCallback((key, targetIndex) => {
    setLayout(prev => {
      const i = prev.findIndex(l => l.key === key);
      if (i < 0 || targetIndex < 0 || targetIndex >= prev.length || i === targetIndex) return prev;
      const next = prev.slice();
      const [item] = next.splice(i, 1);
      next.splice(targetIndex, 0, item);
      return next;
    });
  }, []);
  const reset = useCallback(() => setLayout(defaultLayout(baseColumns)), [baseColumns]);

  return { columns, layout, resize, toggle, move, moveTo, reset, baseByKey };
}
